<template>
  <div id="knowledge-base">
    <v-row>
      <!-- Sidebar with Summary -->
      <v-col cols="12" md="3" lg="3" order="1" order-md="2" class="knowledge-base-summary-overlay">
        <v-card class="knowledge-base-summary">
          <div class="kb-questions py-1">
            <div
              v-for="subTitle in summary"
              :key="subTitle.title"
              class="kb-question d-flex align-start text-sm px-5 my-4 cursor-pointer"
            >
              <v-icon size="14" class="me-2 mt-1" color="secondary">
                {{ icons.mdiCheckboxBlankCircleOutline }}
              </v-icon>
              <span class="text--secondary" @click="scrollToElement(subTitle.link)">{{ subTitle.title }}</span>
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="9" lg="9" order="1" order-md="2">
        <v-card>
          <v-card-text id="init">
            <h2>Mastercard Consumer Clarity Lookups</h2>
          </v-card-text>
          <v-card-text>
            Mastercard Consumer Clarity (MC Clarity) is Mastercard’s dispute-prevention program integrated into the Merchanto Prevent API.
            It enables issuers to request real-time transaction details from enrolled merchants when cardholders question a charge.
          </v-card-text>
          <v-card-text>
            By providing purchase and account details through MC Clarity, merchants can:
            <ul>
              <li>
                Reduce disputes and friendly fraud.
              </li>
              <li>
                Help cardholders recognize transactions instantly.
              </li>
              <li>
                Share subscription information and offer subscription management options.
              </li>
              <li>
                Provide compelling evidence to participate in Mastercard’s First-Party Trust program.
              </li>
            </ul>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-text id="how-works">
            <h2>How It Works</h2>
          </v-card-text>
          <v-card-text>
            When a cardholder questions a transaction, the issuer sends a lookup request to the Merchanto Prevent API. The request is forwarded to your system through the <b>MC Clarity Lookups endpoint</b>. You respond with relevant order and account details, which are returned to the issuer in real time.
          </v-card-text>
          <v-card-text>
            <b>Scenarios covered by MC Clarity Lookups:</b>
            <ul>
              <li>
               <b>Digital / Call Center</b>
                <br>
                Cardholder inquiries through issuer banking apps or call centers. These lookups arrive with <code>originatorChannel = DIGITAL</code> or <code>CALL_CENTRE</code>. Your response helps resolve confusion and prevent unnecessary disputes.
              </li>
              <li>
                <b>Subscription Controls</b>
                <br>
                Cardholders can cancel, pause, or change subscriptions directly from their issuer’s app. If you support subscriptions, the request is sent via the same channel, and your system responds with available options or confirmations.
              </li>
              <li>
                <b>First-Party Trust</b>
                <br>
                For disputes with reason code <b>4837 (No Cardholder Authorization)</b>, Mastercard may request <b>compelling evidence</b> based on your transaction and account history. These lookups arrive with <code>originatorChannel = FIRST_PARTY_TRUST</code>. Providing these details may shift liability back to the issuer.
              </li>
              <li>
                <b>Clarity in Mastercom</b>
                <br>
                Before filing a claim in Mastercom, the issuer reviews your digital receipt. These lookups arrive with <code>originatorChannel = MASTERCOM</code>. A matching order here can stop the dispute before it becomes a chargeback.
              </li>
              <li>
                <b>Transaction Data</b>
                <br>
                Issuers pull Clarity data into their own back-end systems. These lookups arrive with <code>originatorChannel = TRANSACTION_DATA</code>. Respond as you would to any other lookup: the order details you return are the Clarity data the issuer receives.
              </li>
            </ul>
            All of these use cases are supported through a single integration with the Prevent API. The payload and response structure are consistent across request types (<code>DIGITAL</code>, <code>CALL_CENTRE</code>, <code>FIRST_PARTY_TRUST</code>, <code>MASTERCOM</code>, <code>TRANSACTION_DATA</code>).
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="endpoints">1. Endpoint Setup:</v-card-title>
          <v-card-text>
            To participate, merchants must expose an HTTP POST endpoint to receive Clarity lookup requests:
            <ul>
              <li><b>Example:</b> <code>POST /api/prevent/mastercard/orders</code></li>
            </ul>
            This path is only an example. You may define your own endpoint, provided that:
            <ul>
              <li>It accepts <b>POST</b> requests.</li>
              <li>It is secured with <b>HTTPS</b>.</li>
              <li>It does not use query parameters.</li>
              <li>It requires <b>Basic Authentication</b>.</li>
            </ul>
          </v-card-text>
          <v-card-title id="general-req">2. General requirements:</v-card-title>
          <v-card-text>
            Merchanto forwards Mastercard Consumer Clarity lookup requests to your endpoint.
            <br>
            Your system must:
            <ul>
              <li>Validate <b>Basic Auth</b> credentials.</li>
              <li>Respond in JSON with header <code>Content-Type: application/json</code>.</li>
              <li>Return a valid response within <b>1000 ms</b>.</li>
            </ul>
            Examples and schema definitions are available in <b>Swagger</b>, where you can also validate your responses.
          </v-card-text>
          <v-card-title id="general-workflow">3. Workflow</v-card-title>
          <v-card-text>
            <ol>
              <li>A cardholder initiates an inquiry via their issuer’s mobile app, online banking, or call center.</li>
              <li>Mastercard generates a lookup request and sends it to Merchanto.</li>
              <li>Merchanto forwards the request to your provided endpoint.</li>
              <li>Your system matches the request to internal order data and builds a response with the required details.</li>
              <li>
                Your response must always include a <b>responseStatus</b> object to indicate the result of the lookup:
                <ul>
                  <li><code>TRANSACTION_FOUND</code> – transaction located, return details.</li>
                  <li><code>TRANSACTION_NOT_FOUND</code> – no match found.</li>
                  <li><code>MULTIPLE_TRANSACTIONS_FOUND</code> – your matching logic found more than one possible transaction.</li>
                </ul>
              </li>
              <li>Merchanto validates and stores the response, then sends it to Mastercard, which delivers it to the issuer and cardholder in real time.</li>
            </ol>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="transaction-matching">4. Transaction Matching</v-card-title>
          <v-card-text>
            Accurate transaction matching is critical for reducing disputes and maximizing deflection success.
          </v-card-text>
          <v-card-title class="subtitle-1" id="matching-fields">4.1. Matching Fields</v-card-title>
          <v-card-text>
            <v-simple-table>
              <template #default>
                <thead>
                <tr>
                  <th class="text-left">Field Name</th>
                  <th class="text-left">Description</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td><b>Acquirer Reference Number (ARN)</b></td>
                  <td>Acquirer Reference Number of the transaction. <code>acquirerReferenceNumber</code></td>
                </tr>
                <tr>
                  <td><b>Transaction Date and Time</b></td>
                  <td>Date, time, and time zone information of when the transaction was authorized in UTC format. <code>transactionDateTime</code></td>
                </tr>
                <tr>
                  <td><b>Auth Code</b></td>
                  <td>Issuer authorization code for the transaction. <code>issuerAuthorizationCode</code></td>
                </tr>
                <tr>
                  <td><b>Transaction Currency and Amount</b></td>
                  <td>Total amount value associated with the transaction at the time of the authorization and the currency associated with it. <code>transactionAmount</code> + <code>transactionCurrencyCode</code></td>
                </tr>
                <tr>
                  <td><b>Transaction ID / Banknet Reference Number</b></td>
                  <td>Transaction identifier value associated with the type specified in <code>transactionIdentifierType</code>. <code>transactionIdentifierValue</code></td>
                </tr>
                <tr>
                  <td><b>Card BIN</b></td>
                  <td>The first 6 digits of the card used. <code>cardFirstSix</code></td>
                </tr>
                <tr>
                  <td><b>Card Last4</b></td>
                  <td>The last 4 digits of the card used. <code>cardLastFour</code></td>
                </tr>
                <tr>
                  <td><b>Card Acceptor Name</b></td>
                  <td>Merchant's "doing business as" name as seen at the transaction point. <code>cardAcceptorName</code></td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
          <v-card-title class="subtitle-1" id="how-to-implement-matching">4.2. How to implement transaction matching?</v-card-title>
          <v-card-text>
            Not every lookup request includes the full set of identifiers, and some fields (e.g. <b>ARN</b>, <b>Card BIN</b>) may be missing depending on the acquirer.
            To maximise the match rate, apply the following <b>priority-based cascade</b>: try the strongest identifier first, and fall back to weaker combinations only if the previous step returned no result.
            <br><br>
            Return <code>TRANSACTION_FOUND</code> as soon as any condition yields a single matching order.
            If a step returns multiple candidates, either continue to the next (more specific) condition to disambiguate, or respond with <code>MULTIPLE_TRANSACTIONS_FOUND</code>.
            If none of the conditions produce a match, respond with <code>TRANSACTION_NOT_FOUND</code>.
            <br><br>
            Unlike other lookup types, merchants <b>must not return 404 errors</b>.
            <ul>
              <li>Always return <b>HTTP 200</b>.</li>
              <li>Use the <code>responseStatus.code</code> field to indicate the outcome (<code>TRANSACTION_FOUND</code>, <code>TRANSACTION_NOT_FOUND</code>, <code>MULTIPLE_TRANSACTIONS_FOUND</code>).</li>
            </ul>
          </v-card-text>
          <v-card-title class="subtitle-1" id="example-lookup-strategy">4.3. Recommended Lookup Strategy</v-card-title>
          <v-card-text>
            The flowchart and step-by-step grid below show a reference cascade you can adapt: try the strongest identifier first, fall back to weaker combinations only if the previous step returned no single match.
            <br/><br/>
            <div class="text-center my-4">
              <img
                :src="matchingGuidance"
                alt="Recommended Lookup Strategy — flowchart"
                style="max-width: 100%; height: auto;"
              />
            </div>
            <br>
            <b>Amount match:</b> the search amount must equal the stored transaction amount.
            If the search currency and the stored transaction currency differ, first convert the search amount into the transaction's currency and then compare; if the currencies are the same, compare the amounts directly.
            <br><br>
            <b>Guidelines:</b>
            <ul>
              <li>Apply a <b>±3 days tolerance</b> on <code>transactionDate</code> at every step to absorb timezone and settlement differences.</li>
              <li>Prefer <b>authorization records</b> over settled records — Mastercard looks up by authorization identifiers.</li>
              <li>Stop at the first step that yields <b>exactly one</b> match — do not continue evaluating weaker conditions.</li>
            </ul>
            <br>
            <b>Note:</b> some acquirers don't have some fields, e.g. <b>Stripe</b> doesn't have <b>ARN</b> and <b>Card BIN</b>.
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="request-response">5. Request & Response</v-card-title>
          <v-card-title id="request-example"><span class="subtitle-1">5.1. Request Example</span></v-card-title>
          <v-card-text>
            A Clarity Lookup request includes:
            <ul>
              <li><code>requestReference</code> – metadata about the request origin.</li>
              <li><code>searchCriteria</code> – the transaction identifiers and context used to locate the order.</li>
            </ul>
          </v-card-text>
          <v-card-text>
            <b>Example Request:</b>
            <pre class="output"><code>
              {
                "requestReference": {
                  "originatorChannel": "DIGITAL",
                  "originatorId": "420f0845bd9b808abd7acc053b0b372ceb461edd584e9b9db02b35304255715f",
                  "sourceId": "66666688ggg0845bd9b808abd7acc053b0b372ceb461edd584e9b9db02b324gf",
                  "originatorDescription": "Test Issuer",
                  "correlationId": "zebj8evpeo",
                  "locale": "en-US"
                },
                "searchCriteria": {
                  "paymentType": "MC",
                  "transactionIdentifierType": "BANKNET_REF_NUM",
                  "transactionIdentifierValue": "T123456",
                  "acquirerReferenceNumber": "12345678901234567890123",
                  "transactionDateTime": "2020-06-18T17:11:05-05:00",
                  "cardFirstSix": "123456",
                  "cardLastFour": "1234",
                  "issuerAuthorizationCode": "123456",
                  "transactionAmount": "250.35",
                  "transactionCurrencyCode": "USD",
                  "merchantId": "GHF5F67DFSD468GJJXH78",
                  "cardAcceptorId": 12345671234567,
                  "cardAcceptorName": "SomeName",
                  "cardAcceptorLocation": "Austin",
                  "cardAcceptorRegionCode": "TX",
                  "cardAcceptorCountryCode": "US"
                }
              }
            </code></pre>
            <br>
            <b>Field Reference</b>: see <a @click="scrollToElement('mastercard-lookup-request-fields')">8.1. MC Clarity Lookup Request Fields</a> for full descriptions (field, type, availability, description).
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="response-example"><span class="subtitle-1">5.2. Response Example</span></v-card-title>
          <v-card-text>
            A valid merchant response consists of:
            <ul>
              <li><code>responseStatus</code> (required) – lookup result code and description.</li>
              <li><code>order</code> (required if transaction is found) – purchase details: items, prices, delivery, refunds, etc.</li>
              <li><code>merchantProfile</code> (optional) – merchant profile and customer service details.</li>
            </ul>
          </v-card-text>
          <v-card-text>
            <b>Minimum Response Example:</b>
            <pre class="output"><code>
              {
                "responseStatus": {
                  "code": "TRANSACTION_FOUND"
                },
                "order": {
                  "merchantOrderId": "765656JCHJDJ898HYJY90",
                  "orderDateTime": "2025-06-06T09:18:18Z",
                  "subtotal": "39.99",
                  "total": "39.99",
                  "currencyCode": "USD",
                  "orderItems": [
                    {
                      "id": "898989",
                      "productName": "Subscription Pro"
                    }
                  ]
                }
              }
            </code></pre>
            <br>
            <b>Field Reference</b>: see <a @click="scrollToElement('mastercard-lookup-response-fields')">8.2 MC Clarity Lookup Response Fields</a> for detailed schema.
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="http-response-code"><span class="subtitle-1">5.3. HTTP Response Codes</span></v-card-title>
          <v-card-text>
            Your API must respond with the following status codes when handling MC Clarity requests:
            <br><br>
            <v-simple-table>
              <template #default>
                <thead>
                <tr>
                  <th class="text-left">Status Code</th>
                  <th class="text-left">Meaning</th>
                  <th class="text-left">When to Use</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td><b>200 OK</b></td>
                  <td>Success</td>
                  <td>Standard status. Please use it for all cases - transaction found, transaction not found or multiple transactions found.</td>
                </tr>
                <tr>
                  <td><b>400 Bad Request</b></td>
                  <td>Invalid Request</td>
                  <td>The request was understood, but required fields are missing or invalid in your response payload.</td>
                </tr>
                <tr>
                  <td><b>401 Unauthorized</b></td>
                  <td>Authentication Failed</td>
                  <td>The Basic Auth credentials provided in the request were missing or incorrect.</td>
                </tr>
                <tr>
                  <td><b>500 Internal Server Error</b></td>
                  <td>Server Failure</td>
                  <td>Your system encountered an unexpected error and could not process the request.</td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>
            <br>
            <b>Note:</b>
            <ul>
              <li>Always return <b>200</b> with different <code>responseStatus.code</code>.</li>
              <li>Merchanto Platform will validate the payload. Invalid or late responses are not forwarded to Mastercard and will typically result in a dispute.</li>
            </ul>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="lookup-status-mapping"><span class="subtitle-1">5.4. Lookup Status Mapping in Merchanto Platform</span></v-card-title>
          <v-card-text>
            The table below shows how your system’s <b>HTTP response</b> is translated into a <b>lookup status</b> inside the Merchanto Prevent platform. This helps you monitor and reconcile lookups in our dashboard.
            <br><br>
            <v-simple-table>
              <template #default>
                <thead>
                <tr>
                  <th class="text-left">Merchant Response (HTTP)</th>
                  <th class="text-left">Lookup Status in Merchanto</th>
                  <th class="text-left">Description</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td><b>200 OK</b>(valid JSON)<br><code>responseStatus.code = TRANSACTION_FOUND</code></td>
                  <td><b>Accepted / Timeout</b></td>
                  <td>The lookup was successfully matched and returned to Mastercard / did not respond in time.</td>
                </tr>
                <tr>
                  <td><b>200 OK</b>(invalid JSON or missing required fields)</td>
                  <td><b>Invalid</b></td>
                  <td>Response received, but rejected due to schema or content errors.</td>
                </tr>
                <tr>
                  <td><b>200 OK</b><br><code>responseStatus.code = TRANSACTION_NOT_FOUND / MULTIPLE_TRANSACTIONS_FOUND</code></td>
                  <td><b>Not Found</b></td>
                  <td>No matching transaction in merchant system.</td>
                </tr>
                <tr>
                  <td><b>400 Bad Request / 401 Unauthorized / 500 Internal Server Error</b></td>
                  <td><b>Failed</b></td>
                  <td>Lookup was not processed.</td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>
            <br>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="testing">6. Testing</v-card-title>
          <v-card-text>
            Merchanto provides a <b>Swagger environment</b> where you can test your Visa OI integration before going live.
            In <a href="https://dev-api.merchanto.org/api#" target="_blank">Swagger</a>, scroll down to the <b>Prevent Mastercard Clarity API</b> section — you will find two testing endpoints:
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="testing-check-response"><span class="subtitle-1">6.1. Check Response</span></v-card-title>
          <v-card-text>
            This endpoint validates the <b>format of your response payload</b>.
            <br>
            <b>How to use it:</b>
            <ol>
              <li>Click <b>Try It Out</b>.</li>
              <li>
                Paste your JSON payload into the request body textarea.
                <ul>
                  <li>Alternatively, choose one of the predefined examples to see the expected structure.</li>
                </ul>
              </li>
              <li>Click <b>Execute</b>.</li>
              <li>Review the validation result.</li>
            </ol>
          </v-card-text>
          <v-card-text>
            <b>Possible responses:</b>
            <br><br>
            <v-simple-table>
              <template #default>
                <thead>
                <tr>
                  <th class="text-left">Status Code</th>
                  <th class="text-left">Status</th>
                  <th class="text-left">Description</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td><b>200 OK</b></td>
                  <td>Valid</td>
                  <td>Your response is correctly structured and accepted.</td>
                </tr>
                <tr>
                  <td><b>400 Bad Request</b></td>
                  <td>Invalid Payload</td>
                  <td>One or more required fields are missing/invalid. Errors are listed in the response body.</td>
                </tr>
                <tr>
                  <td><b>401 Unauthorized</b></td>
                  <td>Invalid  Credentials</td>
                  <td>Username/password are wrong. (Currently not applied, planned for future use).</td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>
            <br>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="testing-check-flow"><span class="subtitle-1">6.2. Check Flow</span></v-card-title>
          <v-card-text>
            This endpoint emulates the <b>full Mastercard Clarity flow</b> from lookup to response validation.
            <ul>
              <li>The request body contains a <b>predefined object</b> that simulates a Mastercard request.</li>
              <li>You must update its fields with details of a real transaction from your system (<code>transactionAmount</code>, <code>transactionCurrencyCode</code>, <code>issuerAuthorizationCode</code>, <code>transactionIdentifierValue</code>, etc.).</li>
              <li>
                When executed, the system will behave exactly as if Mastercard  sent a live lookup:
                <ul>
                  <li>An order is created on the Merchanto platform.</li>
                  <li>The request is forwarded to your webhook.</li>
                  <li>Your response is validated and logged.</li>
                </ul>
              </li>
            </ul>
            <b>Important requirements:</b>
            <ol>
              <li><code>merchantId </code> — Must be replaced with the value provided by your Merchanto manager. This ensures the request is routed to the correct merchant integration.</li>
              <li>
                <code>transactionIdentifierValue </code> — Must be unique per transaction.
                <ul>
                  <li>To emulate multiple lookups for the <b>same transaction</b>, keep <code>transactionId</code> unchanged.</li>
                  <li>To test different transactions, generate a new <code>transactionIdentifierValue</code> each time.</li>
                </ul>
              </li>
            </ol>
          </v-card-text>
          <v-card-text>
            <b>Possible responses:</b>
            <br><br>
            <v-simple-table>
              <template #default>
                <thead>
                <tr>
                  <th class="text-left">Status Code</th>
                  <th class="text-left">Status</th>
                  <th class="text-left">Description</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td><b>200 OK</b></td>
                  <td>Valid</td>
                  <td>Your system responded with valid transaction details.
                    You can log in to the Merchanto platform, go to <b>Prevent → Orders</b>, and verify the lookup details including response time.</td>
                </tr>
                <tr>
                  <td><b>400 Bad Request</b></td>
                  <td>Invalid Payload</td>
                  <td>The initial test request contained invalid Visa request details. Errors are listed in the response body.</td>
                </tr>
                <tr>
                  <td><b>401 Unauthorized</b></td>
                  <td>Invalid Credentials</td>
                  <td>Username/password are wrong. (Currently not applied, planned for future use).</td>
                </tr>
                <tr>
                  <td><b>503 Service Unavailable</b></td>
                  <td>Invalid Response / System Error</td>
                  <td>
                    Two possible causes:
                    <ul>
                      <li>Your system returned an invalid response.</li>
                      <li>Some system error occurred.</li>
                    </ul>
                    Log in to the Merchanto platform, go to <b>Prevent → Orders</b>, find the order and check the details. If there is no order - it means some internal error occurred. Contact your manager and provide the request/response details.
                  </td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>
            <br>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-text>
            <b>Best practice:</b>
            <ul>
              <li>Always regenerate <code>transactionIdentifierValue</code> for every test.</li>
              <li>Verify all responses inside the <b>Prevent → Orders</b> section of the Merchanto platform for full transparency.</li>
            </ul>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="performance-validation">7. Performance & Validation</v-card-title>
          <v-card-text>
            <ul>
              <li><b>Latency</b>: return responses within <b> &lt; 1000 ms</b>.</li>
              <li>
                <b>Validation</b>: Merchanto validates payload against Visa rules and our API Reference.
                <ul>
                  <li>Invalid/missing mandatory data → <b>not forwarded</b> → likely dispute escalation at the scheme.</li>
                </ul>
              </li>
              <li><b>Security</b>: HTTPS + Basic Auth are mandatory.</li>
            </ul>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="api-reference">8. API Reference</v-card-title>
          <v-card-text>
            This section lists the full schema definitions for Mastercard Consumer Clarity. It is intended for developers who are building and validating the Prevent API integration. Each table describes:
            <ul>
              <li><b>Field name</b></li>
              <li><b>Type</b> (string, number, object, array, etc.)</li>
              <li><b>Constraints</b> (length, format, allowed values)</li>
              <li><b>Description</b> (business meaning and usage)</li>
            </ul>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="mastercard-lookup-request-fields"><span class="subtitle-1">8.1. Mastercard Clarity Lookup Request Fields</span></v-card-title>
          <v-card-text>
            This section defines the fields that Merchanto sends to the merchant when Mastercard initiates a Clarity lookup.
            <ul>
              <li><code>requestReference</code> – identifies the request itself.</li>
              <li><code>searchCriteria</code> – provides the transaction data (card, amount, date, etc.) used to locate the order.</li>
            </ul>
            <br><br>
            <v-simple-table class="payload-example-table">
              <template #default>
                <thead>
                <tr>
                  <th class="text-left">Field</th>
                  <th class="text-left">Type</th>
                  <th class="text-left">Description</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td><b>requestReference*</b></td>
                  <td>
                    <p><i>object (RequestReference Object)</i></p>
                    <p><b>required:</b>&nbsp;<i>true</i></p>
                  </td>
                  <td>The reference details of the order request.</td>
                </tr>
                <tr>
                  <td><b>searchCriteria*</b></td>
                  <td>
                    <p><i>object (SearchCriteria Object)</i></p>
                    <p><b>required:</b>&nbsp;<i>true</i></p>
                  </td>
                  <td>The search criteria for the order request.</td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>
            <br>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-title id="mastercard-lookup-response-fields"><span class="subtitle-1">8.2. Mastercard Clarity Lookup Response Fields</span></v-card-title>
          <v-card-text>
            This section defines the fields that a merchant can return in response to a Clarity lookup.
            <ul>
              <li><code>responseStatus</code> is always required and communicates the result of the lookup (<code>TRANSACTION_FOUND</code>, <code>TRANSACTION_NOT_FOUND</code>, or <code>MULTIPLE_TRANSACTIONS_FOUND</code>).</li>
              <li><code>order</code> should be included whenever the merchant successfully finds a transaction. It becomes optional only when no transaction is found or multiple possible matches exist.</li>
              <li>Other objects such as <code>merchantProfile</code>, <code>accountProfile</code>, etc. are optional, but providing them can improve issuer and cardholder recognition of the transaction and reduce dispute risk.</li>
            </ul>
          </v-card-text>
          <v-card-text>
            <br><br>
            <v-simple-table class="response-example-table">
              <template v-slot:default>
                <thead>
                <tr>
                  <th>Field</th>
                  <th>Details</th>
                  <th>Description</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td><b>responseStatus*</b></td>
                  <td>
                    <p><i>object (ResponseStatus Object)</i></p>
                    <p><b>required:</b>&nbsp;<i>true</i></p>
                  </td>
                  <td>Result status of the request.</td>
                </tr>
                <tr>
                  <td><b>order</b></td>
                  <td>
                    <p><i>object (Order Object)</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                  </td>
                  <td>Contains items, payments, transaction details, shipping, and other information related to the order (purchase).</td>
                </tr>
                <tr>
                  <td><b>merchantProfile</b></td>
                  <td>
                    <p><i>object (MerchantProfile Object)</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                  </td>
                  <td>No Description. See the MerchantProfile Object reference.</td>
                </tr>
                <tr>
                  <td><b>paymentFacilitatorProfile</b></td>
                  <td>
                    <p><i>object (MerchantProfile Object)</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                  </td>
                  <td>No Description. See the MerchantProfile Object reference.</td>
                </tr>
                <tr>
                  <td><b>facilitatorProfile</b></td>
                  <td>
                    <p><i>object (FacilitatorProfile Object)</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                  </td>
                  <td>No Description. See the FacilitatorProfile Object reference.</td>
                </tr>
                <tr>
                  <td><b>accountProfile</b></td>
                  <td>
                    <p><i>object (AccountProfile Object)</i></p>
                    <p><b>required:</b>&nbsp;<i>false</i></p>
                  </td>
                  <td>No Description. See the AccountProfile Object reference.</td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>
            <br>
          </v-card-text>
          <v-card-title id="mastercard-lookup-response-details-fields"><span class="subtitle-1">8.3. Detailed Object Schemas</span></v-card-title>
          <v-card-text>
            For your convenience, the full technical schemas are available as downloadable PDF files.
            You can download the complete <a :href="pdfDocs" download="MastercardClarityAPIReference">Mastercard Clarity Object Schema Reference (PDF)</a> directly from this page.
            <br>
            These documents include:
            <ul>
              <li>Complete field-by-field descriptions for all request and response objects.</li>
              <li>Data type definitions, format and length constraints, and required/optional indicators.</li>
              <li>Object relationships and nesting structures.</li>
              <li>Practical examples for each major object type.</li>
            </ul>
            This approach ensures you always have access to the most up-to-date, version-controlled technical specifications for Mastercard Clarity within the Prevent API.
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mdiCheckboxBlankCircleOutline } from '@mdi/js';
import { ref } from '@vue/composition-api';
import useDocs from '@/views/docs/useDocs';

export default {
  setup() {
    const { scrollToElement } = useDocs();
    const apiUrl = ref('https://dev-api.merchanto.org');
    const pdfDocs = ref(require('@/assets/pdf/MastercardClarityAPIReference.pdf').default);
    const matchingGuidance = ref(require('@/assets/images/docs/ethoca-clarity-matching-guidance.png'));

    const summary = [
      { title: '1. Endpoint Setup', link: 'endpoints' },
      { title: '2. General requirements', link: 'general-req' },
      { title: '3. Workflow', link: 'general-workflow' },
      { title: '4. Transaction Matching', link: 'transaction-matching' },
      { title: '4.1. Matching Fields', link: 'matching-fields' },
      { title: '4.2. How to implement transaction matching?', link: 'how-to-implement-matching' },
      { title: '4.3. Recommended Lookup Strategy', link: 'example-lookup-strategy' },
      { title: '5. Request & Response', link: 'request-response' },
      { title: '5.1. Request Example', link: 'request-example' },
      { title: '5.2. Response Example', link: 'response-example' },
      { title: '5.3. HTTP Response Codes', link: 'http-response-code' },
      { title: '5.4. Lookup Status Mapping in Merchanto Platform', link: 'lookup-status-mapping' },
      { title: '6. Testing', link: 'testing' },
      { title: '6.1. Check Response', link: 'testing-check-response' },
      { title: '6.2. Check Flow', link: 'testing-check-flow' },
      { title: '7. Performance & Validation', link: 'performance-validation' },
      { title: '8. API Reference', link: 'api-reference' },
      { title: '8.1. Mastercard Clarity Lookup Request Fields', link: 'mastercard-lookup-request-fields' },
      { title: '8.2. Mastercard Clarity Lookup Response Fields', link: 'mastercard-lookup-response-fields' },
      { title: '8.3. Detailed Object Schemas', link: 'mastercard-lookup-response-details-fields' },
    ];

    return { scrollToElement, icons: { mdiCheckboxBlankCircleOutline }, apiUrl, summary, pdfDocs, matchingGuidance };
  },
};
</script>

<style scoped lang="scss">
@import '@core/preset/preset/pages/knowledge-base';
</style>
